import React, { useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import _ from 'lodash';
import ScreeningQuestionItem from '../ScreeningQuestionItem/ScreeningQuestionItem';
import InfoGatheringQuestionItem from '../InfoGatheringQuestionItem/InfoGatheringQuestionItem';
import { useDragAndDrop } from '../../../Hooks/useDragAndDrop';
import styles from './QuestionItem.module.scss';
import { getLevel } from '../../../Utils/BotQuestionUtils';
import message from '../BotConfig/ChatbotMessages';
import validateUrl from '../../../Utils/UrlValidator';

const QUESTION_CATEGORIES = {
  FILTERING: 'Filtering',
};

function QuestionItemContainer(props) {
  const {
    question,
    questions,
    dragType,
    index,
    onDragQuestion,
    showFollowupQuestions,
    collapseFollowupQuestions,
    onCategoryChange,
    updateDragQuestionId,
    dragQuestionId,
    ...restProps
  } = props;

  const ref = useRef(null);
  const [inputBoxSelected, setInputBoxSelected] = useState(false);
  const getHelpMessageForQuestion = (questionErrors, questionDetails) => {
    if (!questionErrors?.[questionDetails.Order]) return '';
    const trimmedQuestion = questionDetails?.Question?.trim();
    if (!trimmedQuestion) {
      return <FormattedMessage {...message.questionIsRequiredLabel} />;
    }
    if (validateUrl(trimmedQuestion)) {
      return <FormattedMessage {...message.urlsAreNotAllowedLabel} />;
    }
    return '';
  };

  const level = getLevel(question.Order);

  const followupQuestions = {
    yes: _.find(questions, { Order: `${question.Order}${'.1'}` }),
    no: _.find(questions, { Order: `${question.Order}${'.2'}` }),
  };

  const isDragAllowed =
    Object.keys(showFollowupQuestions)
      .filter(key => !key.includes('.'))
      .every(key => showFollowupQuestions[key] === false) && !inputBoxSelected;

  const [handlerId, drop, drag] = useDragAndDrop({
    itemType: dragType,
    index,
    itemId: question.Id,
    moveCard: onDragQuestion,
    ref,
    isDragAllowed,
    setDragItemId: updateDragQuestionId,
  });
  drag(drop(ref));

  const parentWrapperStyles = {
    marginLeft: `${level * 40}px`,
  };

  const yesFollowupStyles = {
    marginLeft: `${level * 40}px`,
  };

  const noFollowupStyles = {
    marginLeft: `${level * 40}px`,
  };

  const getParentQuestion = () => {
    const commonProps = {
      ...restProps,
      question,
      questions,
      dragType,
      index,
      onDragQuestion,
      dragQuestionId,
      onCategoryChange,
      setInputBoxSelected,
    };

    switch (question.CategoryName) {
      case QUESTION_CATEGORIES.FILTERING:
        return (
          <ScreeningQuestionItem
            {...commonProps}
            showFollowupQuestions={showFollowupQuestions}
            getHelpMessageForQuestion={getHelpMessageForQuestion}
            collapseFollowupQuestions={collapseFollowupQuestions}
          />
        );

      default:
        return (
          <InfoGatheringQuestionItem
            {...commonProps}
            getHelpMessageForQuestion={getHelpMessageForQuestion}
            isDragAllowed={isDragAllowed}
          />
        );
    }
  };

  const renderFollowupQuestion = (type, followupQuestion, customStyles) => {
    const isYes = type === 'yes';
    const messageKey = isYes ? 'yesOnLabel' : 'noOnLabel';
    const circleClass = isYes ? styles.green : styles.red;

    return (
      <>
        <div
          className={`${styles.followupStatement} ${styles[type]}`}
          style={customStyles}
          role="presentation"
          aria-label={`${type} followup for question`}
        >
          <span className={`${circleClass} ${styles.circle}`} aria-hidden="true" />
          <FormattedMessage {...message[messageKey]} values={{ question: question.Question }} />
        </div>
        <QuestionItemContainer
          {...restProps}
          question={followupQuestion}
          questions={questions}
          dragType={dragType}
          index={index}
          onDragQuestion={onDragQuestion}
          order={followupQuestion.Order}
          questionId={followupQuestion.Id}
          showFollowupQuestions={showFollowupQuestions}
          collapseFollowupQuestions={collapseFollowupQuestions}
          onCategoryChange={onCategoryChange}
          updateDragQuestionId={updateDragQuestionId}
          dragQuestionId={dragQuestionId}
        />
      </>
    );
  };

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`${styles.questionItemContainer} ${dragQuestionId === question.Id ? styles.dragging : ''}`}
    >
      <div className={`${styles.parentQuestionWrapper} ${level > 0 ? styles.border : ''}`} style={parentWrapperStyles}>
        {getParentQuestion()}
      </div>

      {followupQuestions.yes &&
        showFollowupQuestions[question.Order] &&
        renderFollowupQuestion('yes', followupQuestions.yes, yesFollowupStyles)}

      {followupQuestions.no &&
        showFollowupQuestions[question.Order] &&
        renderFollowupQuestion('no', followupQuestions.no, noFollowupStyles)}
    </div>
  );
}

export default injectIntl(QuestionItemContainer);
export { QuestionItemContainer as QuestionItemContainerWithoutInjectIntl };
